/**
 * Integrytas Group - Base App (MOBILE)
 * Pages: CSM.ORG.COMPANY / CSM.ORG.DIVISION / CSM.ORG.BUSINESS_UNIT / CSM.ORG.OFFICE
 *        — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/OrgLookupPage.tsx. That desktop file
 * drives all four org-lookup levels from one generic component + four thin wrappers
 * because each level shares the same master + _tl + single-default shape; this file
 * mirrors that shape for mobile: one generic MobileOrgLookupPage driven by a level
 * config, plus MobileCompanyPage / MobileDivisionPage / MobileBusinessUnitPage /
 * MobileOfficePage wrappers registered against the SAME page_internal_codes.
 *
 * DISTINCT presentation (dense card list + facet chips + bottom-sheet detail + edit
 * sheet) over the SAME data/logic layer only:
 *   - reads via apiDb (csm_company/csm_division/csm_business_unit/csm_office + _tl),
 *     resolving the display name against the ACTIVE locale (§8, §17.B4 — never a raw
 *     id or code reaches render),
 *   - every write (activate/deactivate + set-as-default) routes through the Common
 *     Routine via routedUpdate — §2 single write path; never a direct apiDb write.
 * It never re-renders the desktop grid; §16. The desktop still owns translation
 * editing and deletion (roles are shown read-only here, like MobileUserEditSheet).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, SquarePen, Building2, Star } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import { MobileRelatedChildren, type MobileChildGroup, type MobileChildItem } from '../components/MobileRelatedChildren';
import { buildMobileTranslationsGroup } from '../components/mobile_translations_group';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { AuthSession } from '../../types/auth';
import { hasAccess } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';

// ---------------------------------------------------------------------------
// Level configuration — one shape drives all four org lookups (mirrors the
// desktop OrgLookupConfig, trimmed to what the mobile presentation reads).
// ---------------------------------------------------------------------------

interface MobileOrgLevelConfig {
  pageCode: string;   // 'CSM.ORG.COMPANY' — the RBAC page this level maps to
  table: string;      // 'csm_company'
  tlTable: string;    // 'csm_company_tl'
  pkColumn: string;   // 'company_id'
  labelSingular: string;
  labelPlural: string;
}

const COMPANY_CONFIG: MobileOrgLevelConfig = {
  pageCode: 'CSM.ORG.COMPANY',
  table: 'csm_company', tlTable: 'csm_company_tl', pkColumn: 'company_id',
  labelSingular: 'Company', labelPlural: 'Companies',
};
const DIVISION_CONFIG: MobileOrgLevelConfig = {
  pageCode: 'CSM.ORG.DIVISION',
  table: 'csm_division', tlTable: 'csm_division_tl', pkColumn: 'division_id',
  labelSingular: 'Division', labelPlural: 'Divisions',
};
const BUSINESS_UNIT_CONFIG: MobileOrgLevelConfig = {
  pageCode: 'CSM.ORG.BUSINESS_UNIT',
  table: 'csm_business_unit', tlTable: 'csm_business_unit_tl', pkColumn: 'business_unit_id',
  labelSingular: 'Business Unit', labelPlural: 'Business Units',
};
const OFFICE_CONFIG: MobileOrgLevelConfig = {
  pageCode: 'CSM.ORG.OFFICE',
  table: 'csm_office', tlTable: 'csm_office_tl', pkColumn: 'office_id',
  labelSingular: 'Office', labelPlural: 'Offices',
};

interface MobileOrgRow {
  pk: number;
  internal_code: string;
  is_default: 'Y' | 'N';
  is_active: 'Y' | 'N';
  display_text: string;
  /** Every locale's text, kept so the related section can list them (§17.B14). */
  tl: TlRow[];
}

type StatusFacet = 'all' | 'active' | 'inactive';

interface TlRow { locale_code: string; display_text: string }
type RawOrgRow = {
  internal_code: string;
  is_default: 'Y' | 'N';
  is_active: 'Y' | 'N';
} & Record<string, unknown>;

// ---------------------------------------------------------------------------
// Generic list screen
// ---------------------------------------------------------------------------

interface MobileOrgLookupPageProps extends MobilePageComponentProps {
  config: MobileOrgLevelConfig;
}

function MobileOrgLookupPage({ session, config }: MobileOrgLookupPageProps) {
  // §6 RBAC: one component serves four org levels, so the verb is resolved
  // against THIS level's page code from the config, not a file constant.
  const canChange = hasAccess(session, config.pageCode, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [rows, setRows] = useState<MobileOrgRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [defaultOnly, setDefaultOnly] = useState(false);
  const [selected, setSelected] = useState<MobileOrgRow | null>(null);
  const [editing, setEditing] = useState<MobileOrgRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Related children (lazy-loaded when the detail sheet opens).
  const [childGroups, setChildGroups] = useState<MobileChildGroup[]>([]);
  const [childBusy, setChildBusy] = useState(false);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from(config.table)
        .select(`${config.pkColumn}, internal_code, is_default, is_active, ${config.tlTable} ( locale_code, display_text )`)
        .order('internal_code');
      if (e) throw e;

      const list: MobileOrgRow[] = ((data ?? []) as RawOrgRow[]).map((r) => {
        const tls = (r[config.tlTable] as TlRow[] | undefined) ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        return {
          pk: r[config.pkColumn] as number,
          internal_code: r.internal_code,
          is_default: r.is_default,
          is_active: r.is_active,
          display_text: exact?.display_text ?? fb?.display_text ?? r.internal_code,
          tl: tls,
        };
      });
      setRows(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount, when the level changes, and whenever the active locale changes
  // (the _tl display name follows it — §8).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [config.table, activeLocale]);

  // Lazy-load the selected org row's children when the detail sheet opens: the
  // users assigned to it at this org level. The user's org FK matches the level's
  // pk column (company_id / division_id / business_unit_id / office_id), so one
  // wiring serves all four variants. Names shown, never the raw id (§17.B4).
  useEffect(() => {
    if (!selected) { setChildGroups([]); setChildBusy(false); return; }
    const pk = selected.pk;
    let cancelled = false;
    setChildBusy(true);
    void (async () => {
      const byLabel = (a: MobileChildItem, b: MobileChildItem) => a.label.localeCompare(b.label);
      try {
        const { data } = await apiDb
          .from('csm_user_master')
          .select('user_id, display_name, email')
          .eq(config.pkColumn, pk);

        type UserRow = { user_id: number; display_name: string | null; email: string | null };
        const users: MobileChildItem[] = ((data ?? []) as UserRow[])
          .map((u) => ({ id: u.user_id, label: u.display_name ?? u.email ?? `#${u.user_id}`, sub: u.email ?? undefined }))
          .sort(byLabel);

        if (cancelled) return;
        setChildGroups([
          { key: 'users', table: 'csm_user_master', pkColumn: 'user_id', title: t('mobile.org.related.users', 'Users'), items: users },
          // One wiring serves all four org levels — each config carries its own tlTable.
          buildMobileTranslationsGroup(selected.tl, t, activeLocale),
        ]);
      } catch {
        if (!cancelled) setChildGroups([]);
      } finally {
        if (!cancelled) setChildBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, activeLocale]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (defaultOnly && r.is_default !== 'Y') return false;
      if (!q) return true;
      return r.display_text.toLowerCase().includes(q) || r.internal_code.toLowerCase().includes(q);
    });
  }, [rows, filter, statusFacet, defaultOnly]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.org.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const toggleActive = async (r: MobileOrgRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, config.table, config.pkColumn, r.pk, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={config.labelPlural}
        description={t('mobile.org.description', 'Manage {{label}} — the default and active status for each.', { label: config.labelPlural.toLowerCase() })}
      />

      <MobileStatStrip
        stats={[
          { value: rows.length, label: t('mobile.org.stat.total', 'Total') },
          { value: rows.filter((r) => r.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
          { value: rows.filter((r) => r.is_default === 'Y').length, label: t('mobile.org.default', 'Default') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.org.search', 'Search {{label}}', { label: config.labelPlural })}
          aria-label={t('mobile.org.search', 'Search {{label}}', { label: config.labelPlural })}
        />
      </div>

      <MobileFilterChips
        chips={[
          ...statusFacets.map((f) => ({ value: f.key, label: f.label })),
          { value: '__default', label: t('mobile.org.default_only', 'Default') },
        ]}
        isActive={(v) => (v === '__default' ? defaultOnly : statusFacet === v)}
        onSelect={(v) => (v === '__default' ? setDefaultOnly((p) => !p) : setStatusFacet(v as StatusFacet))}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.org.none', 'No {{label}} match', { label: config.labelPlural })}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.pk}
              lead={<Building2 />}
              leadTone={r.is_default === 'Y' ? 'blue' : 'slate'}
              title={r.display_text}
              subtitle={r.internal_code + (r.is_default === 'Y' ? ' · ' + t('mobile.org.default', 'Default') : '')}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.org.detail', '{{label}} detail', { label: config.labelSingular })}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Building2 size={20} /></span>
              <span>
                <div className="m-nm">{selected.display_text}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.org.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.org.default', 'Default')}
                value={
                  selected.is_default === 'Y'
                    ? <MobileStatusPill tone="ok">{t('mobile.org.default', 'Default')}</MobileStatusPill>
                    : t('common.standard', 'Standard')
                }
              />
            </MobileContentCard>
            <MobileRelatedChildren groups={childGroups} busy={childBusy} session={session} />
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.org.edit', 'Edit')}
              </button>)}
              <button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={canChange ? (() => void toggleActive(selected)) : undefined}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileOrgEditSheet
        session={session}
        config={config}
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the master-table scalar flags (Active + Set as default), saved
// through the Common Routine (routedUpdate) — §2. The display name lives in the
// _tl table and translation editing stays on the desktop, so it is shown
// read-only here (mirrors MobileUserEditSheet's read-only roles).
// ---------------------------------------------------------------------------

interface MobileOrgEditSheetProps {
  session: AuthSession;
  config: MobileOrgLevelConfig;
  row: MobileOrgRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function MobileOrgEditSheet({ session, config, row, onClose, onSaved }: MobileOrgEditSheetProps) {
  const { t } = useTranslation();
  const [active, setActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different row is opened.
  useEffect(() => {
    if (!row) return;
    setActive(row.is_active === 'Y');
    setIsDefault(row.is_default === 'Y');
    setError(null);
  }, [row]);

  const save = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks). The DB
    // trigger enforces at-most-one default across the level.
    const { error: e } = await routedUpdate(session, config.table, config.pkColumn, row.pk, {
      is_active: active ? 'Y' : 'N',
      is_default: isDefault ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.org.edit_sheet', 'Edit {{label}}', { label: config.labelSingular })}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Star size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.org.edit_title', 'Edit {{label}}', { label: config.labelSingular })}</div>
                <div className="m-em">{row.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.org.name', 'Name')}</label>
                <div className="m-formrow-ro">
                  {row.display_text}
                  <span className="m-formrow-hint"> · {t('mobile.org.name_desktop', 'edit on desktop')}</span>
                </div>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.org.active_hint', 'Available for assignment')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.org.set_default', 'Set as default')}</div>
                  <div className="m-formrow-s">{t('mobile.org.default_hint', 'Only one {{label}} can be the default', { label: config.labelSingular })}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isDefault}
                  className={'m-switch ' + (isDefault ? 'm-on' : 'm-off')}
                  onClick={() => setIsDefault((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Four preconfigured wrappers for direct registration in MOBILE_PAGE_COMPONENTS.
// ---------------------------------------------------------------------------

export function MobileCompanyPage({ session, onBeginMimic }: MobilePageComponentProps) {
  return <MobileOrgLookupPage session={session} onBeginMimic={onBeginMimic} config={COMPANY_CONFIG} />;
}
export function MobileDivisionPage({ session, onBeginMimic }: MobilePageComponentProps) {
  return <MobileOrgLookupPage session={session} onBeginMimic={onBeginMimic} config={DIVISION_CONFIG} />;
}
export function MobileBusinessUnitPage({ session, onBeginMimic }: MobilePageComponentProps) {
  return <MobileOrgLookupPage session={session} onBeginMimic={onBeginMimic} config={BUSINESS_UNIT_CONFIG} />;
}
export function MobileOfficePage({ session, onBeginMimic }: MobilePageComponentProps) {
  return <MobileOrgLookupPage session={session} onBeginMimic={onBeginMimic} config={OFFICE_CONFIG} />;
}
